import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom"; // To access query parameters
import {
  Container,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import DynamicFileUpload from "../common/DynamicFileUpload"; // The Dynamic File Upload component
import { vendorApi } from "../../api/api";

const AddDocument = () => {
  const [rows, setRows] = useState([
    {
      DocumentOrder: 0,
    }, // Initial row with orderId 0
  ]);
  const [loading, setLoading] = useState(false); // Loader state
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar visibility
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Snackbar severity
  const [isAdditionalDocument, setIsAdditionalDocument] = useState("no"); // Track if a row was added

  // Get query parameters from the URL
  const location = useLocation();
  const {
    PropertyName,
    City,
    PropertyId
  } = location.state || {};
  const [inputValue, setInputValue] = useState(PropertyName); 
  const [CityValue, setCity] = useState(City);


  useEffect(() => {
    // You can use the `propertyId` for any initialization, like fetching property details
    if (PropertyId) {
      
    }
  }, [PropertyId]);

  // Handle snackbar close
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Handle document type change
  const handleDocumentTypeChange = (index, value) => {
    const updatedRows = [...rows];
    updatedRows[index].DocumentType = value;
    setRows(updatedRows);
  };

  // Handle text change
  const handleTextChange = (index, value) => {
    const updatedRows = [...rows];
    updatedRows[index].DocumentName = value;
    setRows(updatedRows);
  };

  // Handle file change
  const handleFileChange = (index, file) => {
    // Check if the file is a PDF
    if (file && file.type !== "application/pdf") {
      setSnackbarMessage("Only PDF files are allowed.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true); // Show error snackbar
      return; // Stop execution if the file is not a PDF
    }

    const updatedRows = [...rows];
    updatedRows[index].file = file; // Store the file in the row object
    setRows(updatedRows);
  };

  // Remove a row
  const removeRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
  };

  // Add a new row
  const addRow = () => {
    // Set isAdditionalDocument to "yes" when a row is added
    setIsAdditionalDocument("yes");

    setRows([
      ...rows,
      {
        DocumentType: "Select Document Type",
        DocumentName: "",
        file: null,
        DocumentOrder: rows.length,
      },
    ]);
  };

  // Handle save
 const handleSave = async () => {
  let hasError = false;
  const updatedRows = rows.map((row, index) => {
    const errors = {};
    if (!row.DocumentType || row.DocumentType === "Select Document Type") {
      errors.DocumentType = "Please select a document type";
      hasError = true;
    }
    if (!row.DocumentName || row.DocumentName.trim() === "") {
      errors.DocumentName = "Please enter a document name";
      hasError = true;
    }
    if (!row.file) {
      errors.file = "Please upload a PDF file";
      hasError = true;
    }
    return { ...row, errors };
  });

  if (hasError) {
    setRows(updatedRows);
    setSnackbarMessage("Please fill all required fields before saving.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
    return;
  }

  // Proceed with uploading logic only if no validation errors
  setLoading(true);
  const fd = new FormData();
  fd.append("isAdditionalDocument", isAdditionalDocument);

  const fileArray = [];

  updatedRows.forEach((row, index) => {
    if (row.file) {
      fileArray.push(row.file);
      const doc = {
        docType: row.DocumentType,
        docName: row.DocumentName,
        docOrder: row.DocumentOrder,
      };
      fd.append("Documents[]", JSON.stringify(doc));
      fd.append(`DocumentType[]`, row.DocumentType);
      fd.append(`DocumentName[]`, row.DocumentName);
    }
  });

  fileArray.forEach((file) => {
    fd.append(`file[]`, file);
  });

  fd.append("propertyId", PropertyId);

  try {
    const response = await vendorApi.addDocument(fd);
    setSnackbarMessage("Documents added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  } catch (error) {
    console.error(error);
    setSnackbarMessage("Failed to add documents. Please try again.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  } finally {
    setLoading(false);
  }
};


  return (
    <Container sx={{ py: 5, maxWidth: "80%" }}>
      {/* Loader */}
      <Backdrop open={loading} sx={{ zIndex: 1300, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ color: "#13216e", fontWeight: 600, fontSize: "24px" }}
        >
          Add Document
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Card sx={{ width: "95%", marginBottom: 3 }}>
          <CardContent>
            <Typography
              variant="h5"
              sx={{ color: "#4e0dff", fontSize: "20px" }}
            >
              {inputValue},
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ color: "#13216e", fontSize: "16px" }}
            >
              {CityValue},
            </Typography>

            <Box sx={{ marginTop: "20px" }}>
              <DynamicFileUpload
                rows={rows}
                setRows={setRows}
                handleTextChange={handleTextChange}
                handleFileChange={handleFileChange}
                handleDocumentTypeChange={handleDocumentTypeChange}
                removeRow={removeRow}
                addRow={addRow}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => setRows([])}
              >
                Reset
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  );
};

export default AddDocument;
